import React, { createContext, useState, useContext, useEffect } from "react";
import axiosInstance from "../axiosInstance";
import axios from "axios";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const establishSessionOnLoad = async () => {
      try {
        const tenantId = sessionStorage.getItem("tenantId");
        const productId = process.env.REACT_APP_PRODUCT_ID;

        const refreshTokenResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/refresh-token`,
          null,
          {
            headers: {
              "X-TenantID": tenantId,
              "X-ProductID": productId,
            },
            withCredentials: true,
          },
        );

        const newJwtToken =
          refreshTokenResponse.headers["Authorization"] ||
          refreshTokenResponse.headers["authorization"];

        if (!newJwtToken) throw new Error("No JWT returned");

        sessionStorage.setItem("jwtToken", newJwtToken);

        const accessResponse = await axiosInstance.get("/api/access");
        setUser(accessResponse.data);
      } catch (error) {
        setUser(null);
        sessionStorage.removeItem("jwtToken");
      } finally {
        setIsLoading(false);
      }
    };

    const tenantId = sessionStorage.getItem("tenantId");
    if (tenantId) establishSessionOnLoad();
    else setIsLoading(false);
  }, []);

  const login = async () => {
    const accessResponse = await axiosInstance.get("/api/access");
    setUser(accessResponse.data);
  };

  const localLogout = () => {
    setUser(null);

    sessionStorage.clear();
    localStorage.clear();

    window.location.replace("/");
  };

  const logout = async () => {
    try {
      await axiosInstance.post("/api/logout");
    } catch (e) {
      console.error(e);
    } finally {
      localLogout();
    }
  };

  // ===========================
  //  ROLE-BASED ACCESS — same as HRM
  // ===========================

  /** Returns true if user has the given role in authorities */
  const hasRole = (role) => user?.authorities?.includes(role) ?? false;

  // ===========================
  //  MODULE / FEATURE ACCESS
  //  (kept for ProtectedRoute usage in App.js)
  // ===========================

  const hasModule = (moduleId) =>
    user?.moduleAccess?.some((m) => m.moduleAccessID === moduleId) ?? false;

  const hasFeature = (featureId) => {
    if (!user?.moduleAccess) return false;
    return user.moduleAccess.some((m) =>
      (m.moduleFeatureAccessIDs || []).includes(featureId),
    );
  };

  const hasModuleFeature = (moduleId, featureId) => {
    const entry = user?.moduleAccess?.find(
      (m) => m.moduleAccessID === moduleId,
    );
    if (!entry) return false;
    return entry.moduleFeatureAccessIDs.includes(featureId);
  };

  /**
   * canAccess — roles are a HARD gate.
   * If roles are specified and user fails them, access is denied
   * regardless of module/feature access.
   */
  const canAccess = ({
    module = null,
    feature = null,
    roles = [],
    requireAll = false,
  } = {}) => {
    if (!user) return false;

    // Roles check — hard gate, same logic as HRM's hasRole
    if (roles.length) {
      const ok = requireAll
        ? roles.every((r) => user.authorities?.includes(r))
        : roles.some((r) => user.authorities?.includes(r));
      if (!ok) return false;
    }

    // Module/feature check (only reached if role check passed)
    if (module && feature) return hasModuleFeature(module, feature);
    if (feature) return hasFeature(feature);
    if (module) return hasModule(module);

    return true;
  };

  /**
   * filterMenuItems — supports both:
   *   - item.roles (new flat format, same as HRM)
   *   - item.auth.roles (legacy nested format)
   */
  const filterMenuItems = (menuItems) =>
    menuItems.filter((item) => {
      const roles = item.auth?.roles ?? item.roles ?? [];
      const module = item.auth?.module ?? item.module ?? null;
      const feature = item.auth?.feature ?? item.feature ?? null;
      const requireAll = item.auth?.requireAll ?? false;
      return canAccess({ roles, module, feature, requireAll });
    });

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    localLogout,

    hasRole,
    hasModule,
    hasFeature,
    hasModuleFeature,
    canAccess,
    filterMenuItems,
  };

  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
